import React, { useContext } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { DataContext } from '../Provider/dataProvider';
import { useFeatureAccess, useElderlyFeatureAccess } from '../hooks/useFeatureAccess';
import { FEATURE_LABELS, FEATURES, hasFeature } from '../constants/featureAccess';
import '../css/featureGate.css';

function resolveLockReason(access, featureKey) {
  const label = FEATURE_LABELS[featureKey] || 'Tính năng này';

  if (!access.isLinked) {
    return {
      title: 'Chưa liên kết gia đình',
      message:
        'Vui lòng liên kết tài khoản người cao tuổi và người thân bằng mã liên kết trước khi sử dụng.',
      primaryLink: '/my-relatives',
      primaryLabel: 'Liên kết ngay',
      secondaryLink: null,
    };
  }

  if (!access.hasMembership) {
    return {
      title: 'Chức năng yêu cầu mua gói thành viên',
      message: `"${label}" cần gói hội viên cho người cao tuổi này. Người thân vui lòng mua gói để tiếp tục.`,
      primaryLink: '/membership',
      primaryLabel: 'Mua ngay',
      secondaryLink: '/my-relatives',
      secondaryLabel: 'Quản lý gia đình',
      reason: 'membership',
    };
  }

  return {
    title: 'Cần gói Nâng cao',
    message: `"${label}" chỉ có trong gói Nâng cao. Nâng cấp để sử dụng điểm danh sinh tồn và toàn bộ tính năng AI.`,
    primaryLink: '/membership',
    primaryLabel: 'Mua ngay',
    secondaryLink: null,
    reason: 'premium',
  };
}

export function FeatureLockCard({ featureKey, compact = false, access: accessOverride }) {
  const { access: globalAccess, loading: globalLoading } = useFeatureAccess();
  const access = accessOverride || globalAccess;
  const loading = accessOverride ? false : globalLoading;

  if (loading) {
    return (
      <div className={`tamcare-feature-lock ${compact ? 'tamcare-feature-lock--compact' : ''}`}>
        <p>Đang kiểm tra quyền truy cập...</p>
      </div>
    );
  }

  const reason = resolveLockReason(access, featureKey);

  return (
    <div className={`tamcare-feature-lock ${compact ? 'tamcare-feature-lock--compact' : ''}`}>
      <div className="tamcare-feature-lock__icon">
        <i className="fa-solid fa-lock" />
      </div>
      <h2>{reason.title}</h2>
      <p>{reason.message}</p>
      <div className="tamcare-feature-lock__actions">
        <Link to={reason.primaryLink} className="tamcare-feature-lock__btn tamcare-feature-lock__btn--primary">
          {reason.primaryLabel}
        </Link>
        {reason.secondaryLink && (
          <Link to={reason.secondaryLink} className="tamcare-feature-lock__btn tamcare-feature-lock__btn--ghost">
            {reason.secondaryLabel}
          </Link>
        )}
      </div>
      {access.tierLabel && (
        <div className="tamcare-feature-lock__badge">
          Gói hiện tại: <strong>{access.tierLabel}</strong>
        </div>
      )}
    </div>
  );
}

export function ElderlyFeatureGate({ elderlyId, feature, children, compact = false }) {
  const { data } = useContext(DataContext);
  const { access, loading, canUse } = useElderlyFeatureAccess(elderlyId);
  const isCareUser = data?.role === 'elderly' || data?.role === 'relative';

  if (!elderlyId || !isCareUser || data?.role === 'admin') {
    return children;
  }

  if (loading) {
    return (
      <div className="tamcare-feature-gate-page">
        <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>Đang kiểm tra quyền truy cập...</p>
      </div>
    );
  }

  if (!canUse(feature)) {
    return (
      <div className={`tamcare-feature-gate-page ${compact ? 'tamcare-feature-gate-page--compact' : ''}`}>
        <FeatureLockCard featureKey={feature} compact={compact} access={access} />
      </div>
    );
  }

  return children;
}

export default function FeatureGate({ feature, children, compact = false, elderlyId: elderlyIdProp, skipPerElderlyCheck = false }) {
  const params = useParams();
  const location = useLocation();
  const routeElderlyId = elderlyIdProp || params.profileId || params.elderlyId || null;
  const { data, authReady } = useContext(DataContext);
  const global = useFeatureAccess();
  const perElderly = useElderlyFeatureAccess(routeElderlyId);
  const isLoggedIn = Boolean(data?.id);
  const isCareUser = data?.role === 'elderly' || data?.role === 'relative';
  const usePerElderly = Boolean(routeElderlyId) && isCareUser && data?.role !== 'admin' && !skipPerElderlyCheck;

  const loading = usePerElderly ? perElderly.loading : global.loading;
  const access = usePerElderly ? perElderly.access : global.access;
  const allowed = data?.role === 'admin' || hasFeature(access, feature);

  if (feature === FEATURES.SHOP) {
    if (!authReady) {
      return (
        <div className="tamcare-feature-gate-page">
          <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>Đang kiểm tra phiên đăng nhập...</p>
        </div>
      );
    }
    if (!isLoggedIn) {
      return <Navigate to="/login" replace state={{ from: location.pathname }} />;
    }
  }

  if (!isLoggedIn || data?.role === 'admin') {
    return children;
  }

  if (loading) {
    return (
      <div className="tamcare-feature-gate-page">
        <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>Đang kiểm tra quyền truy cập...</p>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className={`tamcare-feature-gate-page ${compact ? 'tamcare-feature-gate-page--compact' : ''}`}>
        <FeatureLockCard featureKey={feature} compact={compact} access={access} />
      </div>
    );
  }

  return children;
}
